import OCGatewayClient from "../client/OCGatewayClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RfidProfile } from "../models/RfidProfile";
import { gatewayRealtimeQueryOptions, gatewayUserScopedQueryOptions } from "./gatewayQueryUtils";

let subscribed = false;

export function useRfidProfileQuery(rfidCode: string, deviceId?: string) {
    const queryClient = useQueryClient();
    
    const query =  useQuery({
        queryKey: ['getRfidProfile', rfidCode, deviceId].filter(Boolean),
        queryFn: async () => {
            return OCGatewayClient.request('getRfidProfile', {
                rfidCode,
                deviceId,
                subscribe: true
            }) as Promise<RfidProfile>;
        },
        ...gatewayRealtimeQueryOptions,
        staleTime: 1000 * 60 * 5
    });

    if (!subscribed) {
        OCGatewayClient.socket.on('rfidProfileUpdate', (data: any) => {
            console.log('rfidProfileUpdate, invalidating query', data);
            queryClient.invalidateQueries({
                queryKey: ['getRfidProfile', data.rfidCode]
            });
            queryClient.invalidateQueries({
                queryKey: ['getRfidProfiles']
            });
        });

        subscribed = true;
    }

    return query;
}

/**
 * The household's pet profiles: one merged profile per RFID code, from every
 * user sharing a device with the requester. contributedUserIds tells which
 * profiles the current user has a contribution row of their own in.
 */
export function useRfidProfilesQuery() {
    const query = useQuery({
        queryKey: ['getRfidProfiles'],
        queryFn: async () => {
            return OCGatewayClient.request('getRfidProfiles', {}) as Promise<RfidProfile[]>;
        },
        ...gatewayUserScopedQueryOptions
    });

    return query;
}

export function useRfidProfileMutation() {
    const queryClient = useQueryClient();
    
    const mutation = useMutation({
        mutationFn: async (rfidProfile: any) => {
            return OCGatewayClient.request('updateRfidProfile', rfidProfile);
        },
        onMutate: async (rfidProfile: any) => {
            await queryClient.cancelQueries({ queryKey: ['getRfidProfile', rfidProfile.rfidCode] });

            const previousRfidProfile = queryClient.getQueryData(['getRfidProfile', rfidProfile.rfidCode]);

            queryClient.setQueryData(['getRfidProfile', rfidProfile.rfidCode], (queryData: any) => {
                return Object.assign({}, queryData, rfidProfile);
            });

            return { previousRfidProfile };
        },
        onError: (error, variables, context) => {
            queryClient.setQueryData(['getRfidProfile', variables.rfidCode], context?.previousRfidProfile);
        },
        onSettled: (data, error, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['getRfidProfile', variables.rfidCode] });
            queryClient.invalidateQueries({ queryKey: ['getRfidProfiles'] });
        }
    });

    return mutation;
};

/**
 * Delete the current user's own contribution row for an RFID code. Other
 * household members' contributions - and what they see of the merged
 * profile - are unaffected.
 */
export function useDeleteRfidProfileMutation() {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (rfidCode: string) => {
            return OCGatewayClient.request('deleteRfidProfile', { rfidCode });
        },
        onSettled: (data, error, rfidCode) => {
            queryClient.invalidateQueries({ queryKey: ['getRfidProfile', rfidCode] });
            queryClient.invalidateQueries({ queryKey: ['getRfidProfiles'] });
        }
    });

    return mutation;
};
